import { format, parseISO } from "date-fns";
import ReactECharts from "echarts-for-react";
import PropTypes from "prop-types";
import React, { useMemo } from "react";

const HRV_COLORS = {
  hrv: "#9B59B6", // Purple for HRV
  bpm: "#FF6B6B", // Red for heart rate
};

const DATE_FORMATS = {
  week: "dd/MM",
  month: "dd/MM",
  "6month": "MMM",
  year: "MMM",
  default: "HH:mm",
};

const getDateFormat = (range) => DATE_FORMATS[range] || DATE_FORMATS.default;

function formatChartData(dataPoints, range) {
  const labels = [];
  const hrvData = [];
  const bpmData = [];

  (dataPoints || []).forEach((item) => {
    const label = format(parseISO(item.timestamp), getDateFormat(range));
    labels.push(label);
    hrvData.push(item.hrv);
    bpmData.push(item.bpm);
  });

  return { labels, hrvData, bpmData };
}

function HRVLineChart({ hrvData }) {
  const chartData = useMemo(() => {
    if (!hrvData?.data_points) {
      return { labels: [], hrvData: [], bpmData: [] };
    }
    return formatChartData(hrvData.data_points, hrvData.range);
  }, [hrvData]);

  // Check if we have any BPM data to display
  const hasBpmData = chartData.bpmData.some(
    (val) => val !== null && val !== undefined
  );

  const option = useMemo(
    () => ({
      tooltip: {
        trigger: "axis",
        formatter: (params) => {
          if (!params || params.length === 0) return "";
          const time = params[0].axisValue;
          let content = `<strong>${time}</strong><br/>`;
          params.forEach((param) => {
            if (param.value !== null && param.value !== undefined) {
              const unit = param.seriesName === "HRV" ? "ms" : "bpm";
              content += `<span style="color:${param.color}">\u25CF</span> ${param.seriesName}: ${param.value} ${unit}<br/>`;
            }
          });
          return content;
        },
      },
      legend: {
        data: hasBpmData ? ["HRV", "Heart Rate"] : ["HRV"],
        bottom: 0,
        textStyle: {
          fontSize: 11,
          color: "#666",
        },
      },
      grid: {
        left: 45,
        right: hasBpmData ? 45 : 15,
        top: 20,
        bottom: 50,
      },
      xAxis: {
        type: "category",
        data: chartData.labels,
        axisLabel: {
          fontSize: 10,
          color: "#888",
          interval: "auto",
          rotate: 0,
        },
        axisLine: {
          lineStyle: { color: "#E0E0E0" },
        },
        axisTick: {
          show: false,
        },
      },
      yAxis: [
        {
          type: "value",
          name: "HRV (ms)",
          min: 0,
          max: 150,
          position: "left",
          axisLabel: {
            fontSize: 10,
            color: "#888",
            formatter: "{value}",
          },
          splitLine: {
            lineStyle: { color: "#F0F0F0" },
          },
          nameTextStyle: {
            fontSize: 10,
            color: "#888",
          },
        },
        ...(hasBpmData
          ? [
              {
                type: "value",
                name: "BPM",
                min: 40,
                max: 120,
                position: "right",
                axisLabel: {
                  fontSize: 10,
                  color: "#888",
                  formatter: "{value}",
                },
                splitLine: {
                  show: false,
                },
                nameTextStyle: {
                  fontSize: 10,
                  color: "#888",
                },
              },
            ]
          : []),
      ],
      series: [
        {
          name: "HRV",
          type: "line",
          yAxisIndex: 0,
          data: chartData.hrvData,
          smooth: true,
          symbol: "circle",
          symbolSize: 6,
          lineStyle: {
            color: HRV_COLORS.hrv,
            width: 2,
          },
          itemStyle: {
            color: HRV_COLORS.hrv,
          },
          connectNulls: true,
        },
        ...(hasBpmData
          ? [
              {
                name: "Heart Rate",
                type: "line",
                yAxisIndex: 1,
                data: chartData.bpmData,
                smooth: true,
                symbol: "circle",
                symbolSize: 6,
                lineStyle: {
                  color: HRV_COLORS.bpm,
                  width: 2,
                },
                itemStyle: {
                  color: HRV_COLORS.bpm,
                },
                connectNulls: true,
              },
            ]
          : []),
      ],
    }),
    [chartData, hasBpmData]
  );

  return (
    <ReactECharts
      option={option}
      style={{ height: 320, width: "100%" }}
      opts={{ renderer: "svg" }}
    />
  );
}

HRVLineChart.propTypes = {
  hrvData: PropTypes.shape({
    range: PropTypes.string,
    data_points: PropTypes.arrayOf(
      PropTypes.shape({
        timestamp: PropTypes.string,
        hrv: PropTypes.number,
        bpm: PropTypes.number,
      })
    ),
  }),
};

HRVLineChart.defaultProps = {
  hrvData: {
    range: "day",
    data_points: [],
  },
};

export default HRVLineChart;
